import {useEffect} from "react";
import {Routes, Route, Navigate} from "react-router-dom";
import {common as strings} from "strings";

import Header from "components/Header";

import {useAuthenticatedUser} from "lib/auth";
import {getLocalStorageOrDefault, saveLocalStorage} from "lib/storage";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import AccountPage from "pages/AccountPage";
import AppointmentDetailsPage from "pages/AppointmentDetailsPage";
import AppointmentPatientSelectPage from "pages/AppointmentPatientSelectPage";
import AppointmentQuestionnairePage from "pages/AppointmentQuestionnairePage";
import AppointmentsPage from "pages/AppointmentsPage";
import ChangePasswordPage from "pages/ChangePasswordPage";
import ConfirmTransferPage from "pages/ConfirmTransferPage";
import DemoPage from "pages/DemoPage";
import ForgotEmailPage from "pages/ForgotEmailPage";
import ForgotPasswordPage from "pages/ForgotPasswordPage";
import GuestAppointmentInfoPage from "pages/GuestAppointmentInfoPage";
import HomePage from "pages/HomePage";
import InsurancePage from "pages/InsurancePage";
import LoginPage from "pages/LoginPage";
import MedicationRefillUnauthPage from "pages/MedicationRefillUnauthPage";
import MedicationsPage from "pages/MedicationsPage";
import NewAppointmentPage from "pages/NewAppointmentPage";
import PharmacyPage from "pages/PharmacyPage";
import PrescriptionDetailsPage from "pages/PrescriptionDetailsPage";
import ProfilePage from "pages/ProfilePage";
import ProfileUnauthPage from "pages/ProfileUnauthPage";
import RedirectPage from "pages/RedirectPage";
import RegisterPage from "pages/RegisterPage";
import ResetPasswordPage from "pages/ResetPasswordPage";
import ScheduleAppointmentPage from "pages/ScheduleAppointmentPage";
import SelectAppointmentCampaignPage from "pages/SelectAppointmentCampaignPage";
import SelectAppointmentOptionPage from "pages/SelectAppointmentOptionPage";
import TaxReportPage from "pages/TaxReportPage";
import TransferPharmacyPage from "pages/TransferPharmacyPage";
import UnauthAppointmentsPage from "pages/UnauthAppointmentsPage";
import VerifyPage from "pages/VerifyPage";
import YourPharmacyPage from "pages/YourPharmacyPage";
import MainLayout from "pages/layouts/MainLayout";

import styles from "App.module.css";

const primaryColorKey = "primaryColor";
const defaultBrandColor = getLocalStorageOrDefault(primaryColorKey, "#b00002");

const INVALID_PHARM_ID = 0;
const PHARMACY_SEARCH_URL = "https://search.rx365.com/";

function App() {
  const [pharmacy] = useCurrentPharmacy();

  useEffect(() => {
    const primaryColor = pharmacy?.brandColor ?? defaultBrandColor;
    const secondaryColor = `${primaryColor}0A`;
    const htmlElem = document.querySelector("html");
    htmlElem?.style.setProperty("--brand-primary-color", primaryColor);
    htmlElem?.style.setProperty("--brand-secondary-color", secondaryColor);
    saveLocalStorage(primaryColorKey, primaryColor);
  }, [pharmacy]);

  useEffect(() => {
    if (!pharmacy?.name) {
      return;
    }
    document.title = pharmacy.name;
  }, [pharmacy?.name]);

  useEffect(() => {
    if (
      pharmacy?.id === INVALID_PHARM_ID &&
      process.env.NODE_ENV !== "development"
    ) {
      // don't redirect if dev bc often run from localhost
      window.location.replace(PHARMACY_SEARCH_URL);
    }
  });

  const [authenticatedUser, loadingUser] = useAuthenticatedUser();
  if (loadingUser) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Header
        pharmacyName={pharmacy?.name || strings.rx365}
        pharmacyLogoPath={pharmacy?.storeLogoUrl || ""}
      />
      <Routes>
        <Route path="demo" element={<DemoPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route
          path="/"
          element={
            authenticatedUser ? (
              <Navigate to="/pharmacy" replace />
            ) : (
              <HomePage />
            )
          }
        />
        <Route path="redirect" element={<RedirectPage />} />
        <Route path="/pharmacy" element={<MainLayout />}>
          <Route path="" element={<PharmacyPage />} />
        </Route>
        {pharmacy?.appointmentsEnabled ? (
          <Route path="/appointments" element={<MainLayout />}>
            <Route
              path=""
              element={
                authenticatedUser ? (
                  <AppointmentsPage />
                ) : (
                  <UnauthAppointmentsPage />
                )
              }
            />
            <Route
              path="questionnaire"
              element={<AppointmentQuestionnairePage />}
            />
            <Route path="new" element={<NewAppointmentPage />} />
            <Route
              path="select-campaign"
              element={<SelectAppointmentCampaignPage />}
            />
            <Route
              path="select-option"
              element={<SelectAppointmentOptionPage />}
            />
            <Route path="schedule" element={<ScheduleAppointmentPage />} />
            {authenticatedUser ? (
              <>
                <Route path=":id" element={<AppointmentDetailsPage />} />
                <Route
                  path="patient-selection"
                  element={<AppointmentPatientSelectPage />}
                />
              </>
            ) : (
              <Route path="guest" element={<GuestAppointmentInfoPage />} />
            )}
          </Route>
        ) : null}
        <Route path="/medications" element={<MainLayout />}>
          <Route
            path=""
            element={
              authenticatedUser ? (
                <MedicationsPage />
              ) : (
                <MedicationRefillUnauthPage />
              )
            }
          />
        </Route>
        <Route path="/medications" element={<MainLayout />}>
          <Route path=":id" element={<PrescriptionDetailsPage />} />
        </Route>
        {authenticatedUser ? (
          <Route path="/profile" element={<MainLayout />}>
            <Route path="" element={<ProfilePage />} />
            <Route path="pharmacy" element={<YourPharmacyPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="insurance" element={<InsurancePage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
            <Route path="tax-report" element={<TaxReportPage />} />
            <Route path="transfer-in" element={<TransferPharmacyPage />} />
            <Route
              path="transfer-in/review"
              element={<ConfirmTransferPage />}
            />
          </Route>
        ) : (
          <>
            <Route path="/profile" element={<MainLayout />}>
              <Route path="" element={<ProfileUnauthPage />} />
            </Route>
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="forgot-email" element={<ForgotEmailPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="verify" element={<VerifyPage />} />
          </>
        )}
      </Routes>
    </div>
  );
}

export default App;
